import type { ReactNode } from 'react';
import Pencil from 'svg/Pencil';
import classNames from 'styles/utils/classNames';
import EmptyAddPlaceholder from '../EmptyAddPlaceholder';
import RoundIconButton from '../RoundIconButton';

interface Props {
  title: string;
  /** Rendered next to the title, e.g. the "• 3 locations" count. */
  titleSuffix?: ReactNode;
  children?: ReactNode;
  /** Owners get an edit affordance and see empty sections; visitors don't. */
  isViewerProfile?: boolean;
  onEdit?: () => void;
  /** True when there is nothing to show, which decides between body and placeholder. */
  isEmpty?: boolean;
  /** Owner-only call to action inside the dashed placeholder, e.g. "Add locations". */
  emptyLabel?: string;
  /** Placeholder height: `md` for text sections, `lg` for media (gallery, map). */
  emptySize?: 'md' | 'lg';
  /** `sm` is for sections nested under another heading, e.g. inside About Me. */
  titleSize?: 'sm' | 'lg';
  className?: string;
}

/** Only desktop separates the two levels; mobile runs one heading size throughout. */
const TITLE_SIZES: Record<'sm' | 'lg', string> = {
  sm: 'md:typography-product-card-title-desktop',
  lg: 'md:typography-product-heading-desktop',
};

/**
 * The section frame every profile block shares. It also encodes the one rule that
 * separates the two designs: an empty section is a dashed "add" prompt for the
 * owner and is dropped entirely for everyone else, so a sparse profile reads as a
 * short page rather than a list of blank headings.
 */
export default function ProfileSection({
  title,
  titleSuffix,
  children,
  isViewerProfile = false,
  onEdit,
  isEmpty = false,
  emptyLabel,
  emptySize = 'md',
  titleSize = 'lg',
  className,
}: Props) {
  if (isEmpty && !isViewerProfile) return null;

  return (
    <section className={classNames('flex flex-col gap-ds-md md:gap-ds-lg', className)}>
      <div className="flex items-center justify-between gap-ds-md">
        <h2
          className={classNames(
            'typography-product-subheading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
            TITLE_SIZES[titleSize],
          )}
        >
          {title}
          {titleSuffix}
        </h2>
        {isViewerProfile && onEdit && (
          <RoundIconButton
            label={`Edit ${title}`}
            onClick={onEdit}
            icon={<Pencil className="h-4 w-4" />}
          />
        )}
      </div>

      {isEmpty ? (
        <EmptyAddPlaceholder
          label={emptyLabel ?? ''}
          onClick={onEdit}
          size={emptySize}
        />
      ) : (
        children
      )}
    </section>
  );
}
